/**
 * Tenant settings + currency API wrapper (v2 — Project Independence, DNKADM-0050).
 * DNKADM-0374: adds `billingAnchorDay` (billing day of month, 1–28).
 *   PUT sentinel: omitted/null = leave stored value; `0` = clear; `1`–`28` = set.
 *   GET: `null` when unset — never `0` (out-of-range/zero parses as null).
 * DNKADM-0409: adds `creditPurchaseMembersOnly` (DNKAPI-0566) — absent/mistyped
 *   parses as `true`, the server default; PUT omit = leave unchanged.
 * DNKADM-0432: adds `unconfirmedCancelFullRefund` (DNKAPI-0622) — absent/mistyped
 *   parses as `false`, the server default; PUT omit = leave unchanged.
 * DNKADM-0461: adds `assistantEnabled` (Ask Dink on/off per tenant) — absent/mistyped
 *   parses as `false`, the server default; PUT omit = leave unchanged.
 * DNKADM-0490: adds `coachesSetOwnHours` (DNKAPI-0708, "Coaches can set their own hours")
 *   — absent/mistyped parses as `false` (FAIL-CLOSED, unlike the server's ON default):
 *   My schedule edits only on an explicit `true`, so an unreadable value keeps the hours
 *   read-only rather than offering a Save the server would refuse. PUT omit = unchanged.
 *
 * The one-row-per-tenant booking config plus the setup-only currency editor
 * (handoff §5.1). New module; no v1 equivalent. Bearer-only `/v2/admin/**`
 * surface — `request()` routes it (DNKADM-0049 `isV2Surface`): bearer token, NO
 * CSRF, NO cookies, the bounded 401→refresh→retry. The mutating-audit
 * `X-Session-Mode` stamp fires on the two PUTs (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.1):**
 *  - `GET/PUT /v2/admin/tenant-settings` — the booking config row.
 *  - `PUT /v2/admin/tenant-settings/currency` — the setup-only currency change.
 *
 * **Currency rules (§5.1):**
 *  - `currency` is `CHAR(3)`, **read-only on the main PUT** (stripped server-side).
 *    {@link putTenantSettings} NEVER sends the `currency` key (`exactOptionalProperty
 *    Types`: the key is simply never assigned).
 *  - Currency changes ONLY via {@link putTenantCurrency}, and ONLY during setup. It
 *    locks permanently on the first reservation/payment; afterwards that endpoint
 *    returns `409 CURRENCY_LOCKED`.
 *  - **Lock signal:** we assume `GET /tenant-settings` returns `currencyLocked:
 *    boolean`. If absent, the parse degrades it to `undefined` (lock-state unknown
 *    from the GET) and a `409 CURRENCY_LOCKED` from the currency PUT is the
 *    authoritative fallback (the screen flips to locked on it). Assumption recorded
 *    in the DNKADM-0050 delivery.
 *
 * GET parsing is DEFENSIVE (mirrors `membershipSettings.ts`): a missing/wrong-typed
 * field degrades to the documented server default, never a throw. The fine-grained
 * v2 error code is read with `v2ErrorCode()` (`./v2Errors`); the human text comes
 * from `serverMessage()`.
 */
import { request } from './client';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';
import { asLeadTimeMinutes } from '@/domain/leadTime';
import {
  normalizeStaffCalendarProvider,
  type StaffCalendarProvider,
} from '@/utils/whitelabel';

/** v2 admin tenant-settings base path (bearer surface). */
const PATH = '/v2/admin/tenant-settings';
/** The setup-only currency-change endpoint. */
const CURRENCY_PATH = `${PATH}/currency`;

/** Documented server defaults (handoff §5.1). */
const DEFAULT_SERVICE_INTERVAL_MINUTES = 30;
const DEFAULT_BOOKING_WINDOW_MONTHS = 3;
const DEFAULT_GUEST_HOLD_TTL_MINUTES = 10;
const DEFAULT_CURRENCY = 'USD';
const DEFAULT_CREDIT_PURCHASE_MEMBERS_ONLY = true;
const DEFAULT_UNCONFIRMED_CANCEL_FULL_REFUND = false;
const DEFAULT_ASSISTANT_ENABLED = false;

const HOLD_AUTO_ACTION_FIELDS = {
  minutes: 'holdAutoActionMinutes',
  action: 'holdAutoAction',
} as const;

export type HoldAutoAction = 'SHOWED' | 'NO_SHOW';

/**
 * Parsed `GET /v2/admin/tenant-settings` (handoff §5.1). One row per tenant; the
 * server resolves concrete values, but we still parse defensively and fall back to
 * the documented defaults if a field is missing/mistyped.
 */
export interface TenantSettingsView {
  /** Tenant currency (ISO-4217, CHAR(3)). Read-only on the main PUT. */
  currency: string;
  /**
   * The lock signal (assumed — §5.1): `true` once the tenant has financial
   * history and the currency is permanently locked. `undefined` when the GET does
   * not carry the field, in which case the lock state is unknown from the GET and
   * a `409 CURRENCY_LOCKED` from the currency PUT is the authoritative fallback.
   */
  currencyLocked?: boolean;
  /** Default IANA timezone (e.g. `America/New_York`). */
  defaultTimezone: string;
  /** Slot granularity in minutes (default 30). */
  serviceIntervalMinutes: number;
  /** How far ahead members may book, in months (default 3). */
  bookingWindowMonths: number;
  /** Guest-hold TTL in minutes (default 10). */
  guestHoldTtlMinutes: number;
  /** Minutes after a hold reservation ends before the server auto-actions it; 0 = off. */
  holdAutoActionMinutes: number;
  /** Auto-action to apply when enabled, or null when off/absent/unrecognized. */
  holdAutoAction: HoldAutoAction | null;
  /**
   * ISO 3166-1 alpha-2 country (DNKAPI-0420, DNKADM-0267), or `null` until the
   * tenant configures it. Parsed to exactly two uppercase letters; anything else
   * degrades to `null`.
   */
  countryCode: string | null;
  /**
   * Billing day of month for new membership subscriptions (DNKADM-0374, DNKAPI-0531).
   * `1`–`28` = that day; `null` = not set (charge at signup). GET never returns `0`
   * — any `0` or out-of-range value on the wire is parsed as `null`.
   */
  billingAnchorDay: number | null;
  /**
   * Tenant-wide booking lead time in minutes (DNKADM-0385, DNKAPI-0539): the minimum
   * notice before a slot's start at which a member may still book it. `null` = none
   * (members can book right up to the start). GET never returns `0` — any `0` or
   * out-of-range value on the wire parses as `null`. A service override wins when set.
   */
  leadTimeMinutes: number | null;
  /**
   * The staff-calendar provider (DNKADM-0384 U1, DNKAPI-0504/0538): where coach
   * bookings are mirrored — `'none'` (off, the default), through the white-labeled
   * sub-account, or direct Google. A missing/unknown wire value degrades to `'none'`:
   * an unparseable value must never read as "syncing".
   */
  staffCalendarProvider: StaffCalendarProvider;
  /**
   * Member-app credit purchases (DNKADM-0409, DNKAPI-0566): `true` = only people with a
   * membership can buy credits in the member app; `false` = anyone signed in can. Desk
   * sales and packages are unaffected. A missing/wrong-typed wire value parses as `true`,
   * matching the server default.
   */
  creditPurchaseMembersOnly: boolean;
  /**
   * Cancellation policy (DNKADM-0432, DNKAPI-0622): `true` = a member who cancels a booking
   * staff have not confirmed yet gets a full refund, even inside the cancellation window.
   * A missing/wrong-typed wire value parses as `false`, matching the server default.
   */
  unconfirmedCancelFullRefund: boolean;
  /**
   * Ask Dink (DNKADM-0461): `true` = this business's team gets the how-to assistant in the
   * top bar. A missing/wrong-typed wire value parses as `false`, the server default.
   */
  assistantEnabled: boolean;
  /**
   * Coaches can set their own hours (DNKADM-0490, DNKAPI-0708): `true` = a signed-in coach
   * edits their own weekly hours on My schedule. Only an explicit wire `true` reads as on;
   * absent/mistyped parses as `false` (fail-closed — see the module doc).
   */
  coachesSetOwnHours: boolean;
}

/**
 * `PUT /v2/admin/tenant-settings` input. **Never carries `currency`** — that is
 * stripped server-side and changed only via {@link putTenantCurrency}. All fields
 * optional on the wire; the form sends the full booking-config record.
 */
export interface TenantSettingsUpdate {
  defaultTimezone?: string;
  serviceIntervalMinutes?: number;
  bookingWindowMonths?: number;
  guestHoldTtlMinutes?: number;
  holdAutoActionMinutes?: number;
  holdAutoAction?: HoldAutoAction | null;
  /**
   * Nullable **partial-update** on the wire (DNKADM-0267): sent only as exactly two
   * uppercase letters (anything else is a server `400`); an omitted key leaves the
   * stored country unchanged. There is NO unset path on this surface — the backend
   * ignores null and rejects a blank.
   */
  countryCode?: string;
  /**
   * Billing anchor day (DNKADM-0374). Sentinel: `0` = clear; `1`–`28` = set;
   * omit the key to leave the stored value unchanged. Never write `null`.
   */
  billingAnchorDay?: number;
  /**
   * Booking lead time (DNKADM-0385). The billing-anchor sentinel: `0` = clear (no lead
   * time); `1`–`LEAD_TIME_MAX_MINUTES` = set; omit the key to leave the stored value
   * unchanged. Never write `null`.
   */
  leadTimeMinutes?: number;
  /**
   * The staff-calendar provider (DNKADM-0384 U1). Partial-update on the wire: sent as
   * its lowercase wire value; omit the key to leave the stored provider unchanged.
   * Changing it never touches any coach's calendar selection (server-side, the
   * per-staff fields are independent of this setting).
   */
  staffCalendarProvider?: StaffCalendarProvider;
  /**
   * Member-app credit purchases (DNKADM-0409). Partial-update on the wire: omit the key
   * to leave the stored switch unchanged.
   */
  creditPurchaseMembersOnly?: boolean;
  /**
   * Cancellation policy (DNKADM-0432). Partial-update on the wire: omit the key to leave
   * the stored switch unchanged.
   */
  unconfirmedCancelFullRefund?: boolean;
  /** Ask Dink on/off (DNKADM-0461). Partial-update on the wire: omit the key to leave it unchanged. */
  assistantEnabled?: boolean;
  /** Coaches set their own hours (DNKADM-0490). Partial-update: omit the key to leave it unchanged. */
  coachesSetOwnHours?: boolean;
}

/**
 * `PUT /v2/admin/tenant-settings/currency` input (§5.1). The optional
 * setup-window price conversion: `convertPrices=true` multiplies all configured
 * prices by `New/Current`, where `conversionRate` is the `"Current:New"` string
 * (e.g. `"500:1"`).
 */
export interface TenantCurrencyUpdate {
  /** The new ISO-4217 currency code (CHAR(3)). */
  currency: string;
  /** Multiply existing configured prices by `New/Current` during the change. */
  convertPrices?: boolean;
  /** The `"Current:New"` rate string (e.g. `"500:1"`); required when converting. */
  conversionRate?: string;
}

// --- Defensive parsing helpers (mirrors membershipSettings.ts) -----------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A finite number (accepts numeric strings), or `null`. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** A positive integer (server defaults are positive); else `null`. */
function asPositiveInt(value: unknown): number | null {
  const n = asNumber(value);
  if (n === null || n <= 0) return null;
  return Math.trunc(n);
}

/** A non-negative integer; else `null`. */
function asNonNegativeInt(value: unknown): number | null {
  const n = asNumber(value);
  if (n === null || n < 0) return null;
  return Math.trunc(n);
}

function asHoldAutoAction(value: unknown): HoldAutoAction | null {
  if (value === 'SHOWED' || value === 'NO_SHOW') return value;
  return null;
}

/** A non-empty trimmed string, or `null`. */
function asNonEmptyString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  return null;
}

/**
 * Normalize a currency code: a 3-letter ISO-4217 code (upper-cased), else the
 * documented `USD` default. The server is the authority on the actual value; this
 * only guards a missing/mistyped field on read.
 */
function asCurrencyCode(value: unknown): string {
  const s = asNonEmptyString(value);
  if (s && /^[A-Za-z]{3}$/.test(s)) return s.toUpperCase();
  return DEFAULT_CURRENCY;
}

/**
 * Parse the lock signal. Present boolean → that value; absent / wrong-typed →
 * `undefined` (lock state unknown from the GET; the `409 CURRENCY_LOCKED` fallback
 * governs). Per the §5.1 assumption.
 */
function asCurrencyLocked(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * Billing anchor day (DNKADM-0374): a whole number in 1–28 (truncated toward zero
 * if a numeric string) → that integer; anything else (absent, 0, out-of-range,
 * non-integer) → `null`. GET never returns `0`; if it ever does, `null` is correct.
 */
function asBillingAnchorDay(value: unknown): number | null {
  const n = asNumber(value);
  if (n === null) return null;
  const t = Math.trunc(n);
  return t >= 1 && t <= 28 ? t : null;
}

/**
 * Parse the ISO 3166-1 alpha-2 country (DNKADM-0267): exactly two letters →
 * uppercased; anything else (absent, blank, mistyped) → `null` (unconfigured).
 */
function asCountryCode(value: unknown): string | null {
  const s = asNonEmptyString(value);
  if (s && /^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();
  return null;
}

function parseSettings(raw: unknown): TenantSettingsView {
  const o = isObject(raw) ? raw : {};
  const currencyLocked = asCurrencyLocked(o['currencyLocked']);
  const holdAutoActionRawMinutes = asNonNegativeInt(o[HOLD_AUTO_ACTION_FIELDS.minutes]) ?? 0;
  const holdAutoAction =
    holdAutoActionRawMinutes > 0 ? asHoldAutoAction(o[HOLD_AUTO_ACTION_FIELDS.action]) : null;
  // exactOptionalPropertyTypes: only assign `currencyLocked` when it is present.
  const view: TenantSettingsView = {
    currency: asCurrencyCode(o['currency']),
    defaultTimezone: asNonEmptyString(o['defaultTimezone']) ?? '',
    serviceIntervalMinutes:
      asPositiveInt(o['serviceIntervalMinutes']) ?? DEFAULT_SERVICE_INTERVAL_MINUTES,
    bookingWindowMonths:
      asPositiveInt(o['bookingWindowMonths']) ?? DEFAULT_BOOKING_WINDOW_MONTHS,
    guestHoldTtlMinutes:
      asPositiveInt(o['guestHoldTtlMinutes']) ?? DEFAULT_GUEST_HOLD_TTL_MINUTES,
    holdAutoActionMinutes: holdAutoAction === null ? 0 : holdAutoActionRawMinutes,
    holdAutoAction,
    countryCode: asCountryCode(o['countryCode']),
    billingAnchorDay: asBillingAnchorDay(o['billingAnchorDay']),
    leadTimeMinutes: asLeadTimeMinutes(o['leadTimeMinutes']),
    staffCalendarProvider: normalizeStaffCalendarProvider(o['staffCalendarProvider']),
    creditPurchaseMembersOnly:
      typeof o['creditPurchaseMembersOnly'] === 'boolean'
        ? o['creditPurchaseMembersOnly']
        : DEFAULT_CREDIT_PURCHASE_MEMBERS_ONLY,
    unconfirmedCancelFullRefund:
      typeof o['unconfirmedCancelFullRefund'] === 'boolean'
        ? o['unconfirmedCancelFullRefund']
        : DEFAULT_UNCONFIRMED_CANCEL_FULL_REFUND,
    assistantEnabled:
      typeof o['assistantEnabled'] === 'boolean'
        ? o['assistantEnabled']
        : DEFAULT_ASSISTANT_ENABLED,
    // Fail-closed (DNKADM-0490): only an explicit `true` lets My schedule offer a Save.
    coachesSetOwnHours: o['coachesSetOwnHours'] === true,
  };
  if (currencyLocked !== undefined) view.currencyLocked = currencyLocked;
  return view;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/tenant-settings` → the tenant's booking config + currency.
 * Defensively parsed (a missing field degrades to the documented default, never
 * throws). Throws `ApiError` on a non-2xx response.
 */
export async function getTenantSettings(): Promise<TenantSettingsView> {
  const body = await request<unknown>('GET', PATH);
  return parseSettings(body);
}

/**
 * `PUT /v2/admin/tenant-settings` → save the booking config. **NEVER sends the
 * `currency` key** (read-only on this surface, §5.1 — `exactOptionalPropertyTypes`
 * keeps it off the body entirely). The form sends the full booking-config record;
 * client-side range validation runs first. Returns the re-parsed view. Throws a
 * typed `ApiError` (`400` VALIDATION inline-able, `403` FORBIDDEN, upstream).
 */
export async function putTenantSettings(
  input: TenantSettingsUpdate,
): Promise<TenantSettingsView> {
  const json: Record<string, unknown> = {};
  if (input.defaultTimezone !== undefined) json['defaultTimezone'] = input.defaultTimezone;
  if (input.serviceIntervalMinutes !== undefined) {
    json['serviceIntervalMinutes'] = input.serviceIntervalMinutes;
  }
  if (input.bookingWindowMonths !== undefined) {
    json['bookingWindowMonths'] = input.bookingWindowMonths;
  }
  if (input.guestHoldTtlMinutes !== undefined) {
    json['guestHoldTtlMinutes'] = input.guestHoldTtlMinutes;
  }
  if (input.holdAutoActionMinutes !== undefined) {
    json[HOLD_AUTO_ACTION_FIELDS.minutes] = input.holdAutoActionMinutes;
  }
  if (input.holdAutoAction !== undefined) {
    json[HOLD_AUTO_ACTION_FIELDS.action] = input.holdAutoAction;
  }
  // Country (DNKADM-0267) — partial-update: only a real 2-uppercase value goes out;
  // an omitted key leaves the stored country unchanged (the backend ignores null).
  if (input.countryCode !== undefined) json['countryCode'] = input.countryCode;
  // Billing anchor day (DNKADM-0374): 0 = clear, 1–28 = set; omit = leave unchanged.
  // Never write null — the form always sends 0 or the day; never null.
  if (input.billingAnchorDay !== undefined) json['billingAnchorDay'] = input.billingAnchorDay;
  // Booking lead time (DNKADM-0385): the same sentinel — 0 = clear, 1..MAX = set; omit =
  // leave unchanged. Never null.
  if (input.leadTimeMinutes !== undefined) json['leadTimeMinutes'] = input.leadTimeMinutes;
  // Staff calendar provider (DNKADM-0384 U1): the lowercase wire value; omit = unchanged.
  if (input.staffCalendarProvider !== undefined) {
    json['staffCalendarProvider'] = input.staffCalendarProvider;
  }
  // Member-app credit purchases (DNKADM-0409): omit = unchanged.
  if (input.creditPurchaseMembersOnly !== undefined) {
    json['creditPurchaseMembersOnly'] = input.creditPurchaseMembersOnly;
  }
  // Unconfirmed-booking cancel refunds in full (DNKADM-0432): omit = unchanged.
  if (input.unconfirmedCancelFullRefund !== undefined) {
    json['unconfirmedCancelFullRefund'] = input.unconfirmedCancelFullRefund;
  }
  // Ask Dink on/off (DNKADM-0461): omit = unchanged.
  if (input.assistantEnabled !== undefined) json['assistantEnabled'] = input.assistantEnabled;
  // Coaches set their own hours (DNKADM-0490): omit = unchanged.
  if (input.coachesSetOwnHours !== undefined) {
    json['coachesSetOwnHours'] = input.coachesSetOwnHours;
  }
  // `currency` is intentionally NEVER assigned — it is stripped server-side and
  // changed only via putTenantCurrency.

  const body = await request<unknown>('PUT', PATH, { json });
  return parseSettings(body);
}

/**
 * `PUT /v2/admin/tenant-settings/currency` → change the tenant currency. Only
 * succeeds during setup (no financial history); once locked the server returns
 * `409 CURRENCY_LOCKED` — detect it with {@link isCurrencyLockedError}. The
 * optional `{ convertPrices, conversionRate }` carries the setup-window price
 * conversion. Returns the re-parsed view. Throws a typed `ApiError`.
 */
export async function putTenantCurrency(
  input: TenantCurrencyUpdate,
): Promise<TenantSettingsView> {
  const json: Record<string, unknown> = { currency: input.currency };
  if (input.convertPrices !== undefined) json['convertPrices'] = input.convertPrices;
  if (input.conversionRate !== undefined) json['conversionRate'] = input.conversionRate;

  const body = await request<unknown>('PUT', CURRENCY_PATH, { json });
  return parseSettings(body);
}

/**
 * True when a thrown error is the authoritative currency lock — the `409
 * CURRENCY_LOCKED` from the currency PUT (the §5.1 fallback lock signal). The
 * screen flips to the locked state on this regardless of the GET's `currencyLocked`.
 */
export function isCurrencyLockedError(error: unknown): boolean {
  return error instanceof ApiError && v2ErrorCode(error) === 'CURRENCY_LOCKED';
}
